import { requireHost, getApiKey, getActiveTeam, reloadFromShellProfile } from './config.js';
import { ApiKeyExchangeError, isTransientExchangeStatus } from './auth-exchange-error.js';

// ENG-8260: re-exported so existing importers of the API client can reach the
// exchange-failure classifier without also importing the leaf module.
export { ApiKeyExchangeError, isTransientAuthFailure, exchangeFailureKind } from './auth-exchange-error.js';

// ENG-6410: stamped into the X-Agt-Cli-Version header on every authenticated
// host-runtime request so the API can record which agt-cli version produced a
// given audit_log row (per-version bug-rate monitoring). Resolved from the same
// tsup `define` build global the rest of the CLI uses; 'dev' in local/unbundled
// runs.
declare const __CLI_VERSION__: string;
const agtCliVersion = typeof __CLI_VERSION__ !== 'undefined' ? __CLI_VERSION__ : 'dev';

// ENG-6412: the authoritative host config_hash is computed API-side (it includes
// server-resolved flags), returned on each /host/heartbeat, and cached here so
// every subsequent authenticated request carries X-Config-Hash. That lets the
// API stamp audit_log.config_hash for host-runtime actions — the same rail as
// X-Agt-Cli-Version above. Null until the first heartbeat returns one (an older
// API omits it ⇒ header absent ⇒ audit config_hash NULL, null-safe).
let lastConfigHash: string | null = null;
export function setConfigHash(hash: string | null): void {
  lastConfigHash = hash && hash.length > 0 ? hash : null;
}

/** Cached exchange result for API key -> JWT. */
let cachedExchange: {
  token: string;
  hostId: string;
  teamId: string;
  teamSlug: string | null;
  framework: string | null;
  hostKind: string | null;
  claudeAuthMode: 'subscription' | 'api_key' | 'openrouter';
  claudeAuthModeRaw: string | null;
  anthropicApiKeyFingerprint: string | null;
  anthropicApiKey: string | null;
  userEmail: string | null;
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
  expiresAt: number;
  /**
   * When this entry was minted. Distinct from `expiresAt`, which is the JWT's
   * own lifetime — `maxAgeMs` callers ask "how stale is what you are telling me
   * about the HOST", and that question is not answerable from a token expiry.
   */
  fetchedAt: number;
} | null = null;

/** Mutex: in-flight exchange promise to prevent concurrent re-exchanges. */
let exchangeInFlight: Promise<ExchangeResult> | null = null;

/**
 * ENG-10093: outstanding forces from `forceNextExchange()`, counted rather than
 * flagged.
 *
 * COUNTERS, NOT A BOOLEAN, for the same reason `createFetchGate` uses them
 * (`fetch-cadence.ts`): an exchange is not atomic, so a rotation push can land
 * after the request goes out and before the response comes back. That response
 * was produced BEFORE the change, and clearing a flag with it would consume the
 * very force the push exists to demand — leaving the old credential in place for
 * a full `maxAgeMs` window, with no error and nothing in the log. A successful
 * exchange consumes exactly the count it observed when it STARTED, so a force
 * that arrives mid-flight survives into the next call.
 */
let exchangeForceCount = 0;
let consumedExchangeForceCount = 0;

/**
 * Make the next exchange go to the network regardless of how recently the cache
 * was filled — WHATEVER CALLER issues it, not only a `maxAgeMs` one.
 *
 * That breadth is deliberate. The force means "what you hold is stale", which is
 * true for every reader of the cache, and it costs nothing extra: the first
 * caller after the force refreshes, and the bounded caller behind it then reads
 * a fresh entry, so one network call serves both. Restricting it to `maxAgeMs`
 * callers would leave the ordinary token lookups serving the credential the push
 * just superseded, for up to the JWT's full lifetime.
 *
 * Deliberately NOT `invalidateExchange()`: that nulls the whole entry, so
 * `getCachedClaudeAuthMode()` would report "mode unknown" until the next
 * exchange landed, and its callers are documented to treat null conservatively.
 * A push must not degrade a synchronous read it was never about.
 */
export function forceNextExchange(): void {
  exchangeForceCount += 1;
}

export interface ExchangeResult {
  token: string;
  hostId: string;
  teamId: string;
  teamSlug: string | null;
  framework: string | null;
  /**
   * ENG-7587 (ADR-0042 Option-1 pt3a): the host's tenancy model ('dedicated' |
   * 'pool'), from the exchange response. null against an older API that doesn't
   * return it — callers must treat null conservatively as 'dedicated' (the
   * shared-host-key behaviour), never as pool.
   */
  hostKind: string | null;
  /**
   * Operator-configured Claude Code auth mode. 'subscription' (default) uses
   * OAuth creds from `claude /login`; 'api_key' uses anthropicApiKey below.
   */
  claudeAuthMode: 'subscription' | 'api_key' | 'openrouter';
  /**
   * ENG-9486: what the SERVER actually said, before the narrowing above.
   *
   * The normalisation is correct and must stay — collapsing an unrecognised
   * mode to `subscription` is the safe default for anything that DECIDES, and
   * the comment at the assignment explains what breaks otherwise. But it makes
   * the narrowed value unfit for DISPLAY: `agt whoami` reported "subscription"
   * for an `openrouter` host, and ADR-0074 adds `gateway_token` and `aws_role`,
   * which would land in the same bucket and read as a subscription too.
   *
   * So: decisions read `claudeAuthMode`, humans read this. Null only if the
   * server omitted the field entirely.
   */
  claudeAuthModeRaw: string | null;
  /**
   * First 8 hex chars of sha256(anthropicApiKey). Always returned when an
   * api_key is stored for this host — the manager uses it to detect key
   * rotation without re-decrypting every poll.
   */
  anthropicApiKeyFingerprint: string | null;
  /**
   * Decrypted Anthropic API key. Populated ONLY when claudeAuthMode=api_key
   * AND decrypt succeeded server-side. NEVER log this — the manager should
   * pass it directly to claude's env and nothing else.
   */
  anthropicApiKey: string | null;
  userEmail: string | null;
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
}

/**
 * ENG-7235: cheap synchronous read of the last-exchanged host Claude auth mode,
 * for host-side gates that must not pay a network round-trip per call (e.g. the
 * per-poll conversation-eval / memory-extraction cost gate). Returns null only
 * before the first /host/exchange of the process; the manager exchanges very
 * early (heartbeat, realtime subscriptions, spawn) so steady state is always
 * populated. claude_auth_mode is a host-level identity (every agent on the host
 * shares it), so a single cached value is authoritative for the whole fleet on
 * this host. Callers should treat null conservatively as "mode unknown" and not
 * change behaviour until it is known.
 */
export function getCachedClaudeAuthMode(): 'subscription' | 'api_key' | 'openrouter' | null {
  return cachedExchange?.claudeAuthMode ?? null;
}

/**
 * Invalidate the cached exchange JWT so the next call re-exchanges.
 */
export function invalidateExchange(): void {
  cachedExchange = null;
  // ENG-6412: the cached config_hash is bound to the host/auth context the last
  // heartbeat ran under. Drop it when the exchange is invalidated (token expiry,
  // key rotation, host re-exchange) so a stale hash can't ride a new auth
  // context until the next heartbeat returns a fresh one.
  lastConfigHash = null;
}

/**
 * Exchange a `tlk_` API key for a short-lived JWT via the Hono API.
 * Concurrent callers share a single in-flight request to avoid races.
 *
 * `forceRefresh: true` bypasses the JWT cache — callers that need to detect
 * server-side state changes (e.g. claude_auth_mode rotation, ENG-4417) must
 * pass this, otherwise they'll read stale values for up to ~50 minutes.
 * Concurrent in-flight requests are still coalesced either way.
 *
 * `maxAgeMs` is the BOUNDED form of that force, added by ENG-10093, and it
 * exists because the unbounded one turned a per-poll call site into the single
 * highest-volume endpoint on the API: `/host/exchange` served 284,884
 * requests/day on 2026-09-05 (10.3% of everything) against a steady-state need
 * of roughly 1.2/hour/host. Pass it when the reason for forcing is "do not act
 * on auth I learned about a long time ago" rather than "this exact call must
 * hit the network". The cache is served while it is younger than `maxAgeMs`;
 * past that, the call goes out.
 *
 * Precedence, stated because the combination is expressible: `forceRefresh`
 * WINS. It is the unconditional form, and a caller that passes both is asking
 * for the network.
 */
export async function exchangeApiKey(
  rawKey: string,
  retried = false,
  opts: { forceRefresh?: boolean; maxAgeMs?: number } = {},
): Promise<ExchangeResult> {
  // Return cached result if still valid (with 60s buffer), the caller didn't
  // explicitly request a refresh, no force is outstanding, and — for a
  // `maxAgeMs` caller — the entry is younger than the age it asked for.
  const withinMaxAge =
    opts.maxAgeMs === undefined ||
    (cachedExchange !== null && Date.now() - cachedExchange.fetchedAt < opts.maxAgeMs);
  const forcePending = exchangeForceCount > consumedExchangeForceCount;
  if (
    !opts.forceRefresh &&
    !forcePending &&
    withinMaxAge &&
    cachedExchange &&
    Date.now() < cachedExchange.expiresAt - 60_000
  ) {
    return {
      token: cachedExchange.token,
      hostId: cachedExchange.hostId,
      teamId: cachedExchange.teamId,
      teamSlug: cachedExchange.teamSlug,
      framework: cachedExchange.framework,
      hostKind: cachedExchange.hostKind,
      claudeAuthMode: cachedExchange.claudeAuthMode,
      claudeAuthModeRaw: cachedExchange.claudeAuthModeRaw,
      anthropicApiKeyFingerprint: cachedExchange.anthropicApiKeyFingerprint,
      anthropicApiKey: cachedExchange.anthropicApiKey,
      userEmail: cachedExchange.userEmail,
      supabaseUrl: cachedExchange.supabaseUrl,
      supabaseAnonKey: cachedExchange.supabaseAnonKey,
    };
  }

  // Captured BEFORE the request goes out, so a force that lands while it is in
  // flight is not consumed by a response that predates it (see the counters'
  // declaration). Read here rather than after the await for that reason alone.
  const observedForceCount = exchangeForceCount;

  // Coalesce concurrent exchange calls into a single request — covers both
  // the natural-expiry refresh and the forceRefresh path.
  if (exchangeInFlight) {
    return exchangeInFlight;
  }

  exchangeInFlight = doExchange(rawKey, retried);
  try {
    const result = await exchangeInFlight;
    // Only a SUCCESSFUL exchange consumes a force. A transport blip must leave
    // the force outstanding so the next pass retries immediately rather than
    // treating the failure as though the refresh had happened.
    consumedExchangeForceCount = observedForceCount;
    return result;
  } finally {
    exchangeInFlight = null;
  }
}

// ENG-8260: a 5xx on /host/exchange is "the API is unwell", not "this key is
// invalid" — but the manager's persistent-session path treated any thrown error
// as an auth verdict and tore down running sessions. Retry transient failures
// here so a short blip never reaches a session-affecting decision at all.
//
// Bounded deliberately: the manager polls on a ~30s cadence and awaits this on
// the spawn path, so the retry budget must stay well inside a tick. Defaults
// give ~0.5s + ~1.0s of sleep across 2 retries (~1.5s worst case, 3 requests
// total), which covers the ~2s alyve-host-1 blip that motivated the ticket
// without risking a tick overrun. Both are tunables, not gates.
const DEFAULT_EXCHANGE_RETRY_ATTEMPTS = 2;
const DEFAULT_EXCHANGE_RETRY_BASE_MS = 500;

function exchangeRetryAttempts(): number {
  const raw = process.env['AGT_AUTH_EXCHANGE_RETRY_ATTEMPTS'];
  const parsed = raw ? parseInt(raw, 10) : NaN;
  // Clamp to [0, 5]: 0 disables retries (restores pre-ENG-8260 timing for a
  // host that needs it), 5 caps the worst case at ~15s.
  if (Number.isFinite(parsed) && parsed >= 0) return Math.min(parsed, 5);
  return DEFAULT_EXCHANGE_RETRY_ATTEMPTS;
}

function exchangeRetryBaseMs(): number {
  const raw = process.env['AGT_AUTH_EXCHANGE_RETRY_BASE_MS'];
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return Math.min(parsed, 5_000);
  return DEFAULT_EXCHANGE_RETRY_BASE_MS;
}

/**
 * Normalise anything thrown out of `attemptExchange` into an
 * `ApiKeyExchangeError`. A raw `fetch` rejection (DNS failure, socket reset,
 * TLS error) has no HTTP status and is transient by definition — the request
 * never reached the API, so it says nothing about the credential.
 */
function asExchangeError(err: unknown): ApiKeyExchangeError {
  if (err instanceof ApiKeyExchangeError) return err;
  const message = err instanceof Error ? err.message : String(err);
  return new ApiKeyExchangeError(message, null, true);
}

/**
 * ENG-8260: retry wrapper around a single exchange attempt. Transient failures
 * (5xx / 429 / transport) are retried with exponential backoff; a verdict
 * (401/403/revoked/400) is rethrown on the first attempt so genuine auth
 * failures are still acted on immediately, exactly as before.
 */
async function doExchange(rawKey: string, retried: boolean): Promise<ExchangeResult> {
  const maxRetries = exchangeRetryAttempts();
  const baseMs = exchangeRetryBaseMs();
  let lastErr: ApiKeyExchangeError | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await attemptExchange(rawKey, retried);
    } catch (err) {
      const exchangeErr = asExchangeError(err);
      if (!exchangeErr.transient || attempt === maxRetries) throw exchangeErr;
      lastErr = exchangeErr;
      await new Promise((resolve) => setTimeout(resolve, baseMs * 2 ** attempt));
    }
  }

  // Unreachable — the loop either returns or throws. Kept so the function is
  // total for the type checker without an `as` cast.
  throw lastErr ?? new ApiKeyExchangeError('API key exchange failed', null, true);
}

async function attemptExchange(rawKey: string, retried: boolean): Promise<ExchangeResult> {
  const res = await fetch(`${requireHost()}/host/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ host_key: rawKey }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as Record<string, unknown>;
    const errorMsg = String(body['error'] ?? res.statusText);
    const host = requireHost();
    const obfuscated = rawKey.length > 12
      ? `${rawKey.slice(0, 8)}${'*'.repeat(rawKey.length - 12)}${rawKey.slice(-4)}`
      : rawKey.slice(0, 4) + '****';

    // 502/503/504 = API unreachable (proxy error, server restarting, etc.)
    // ENG-8260: message text unchanged; now carries the status + transient flag
    // so the manager can tell this apart from a credential verdict.
    if (res.status >= 502 && res.status <= 504) {
      throw new ApiKeyExchangeError(
        `API unreachable (${res.status}): ${host} — is the API server running?`,
        res.status,
        true,
      );
    }

    // If key was revoked, try reloading from shell profile (agt setup may have
    // written a new key that the current process hasn't picked up yet).
    if (errorMsg.includes('revoked') && !retried) {
      reloadFromShellProfile();
      const freshKey = getApiKey();
      if (freshKey && freshKey !== rawKey) {
        // ENG-8260 (CodeRabbit, PR #3900): call `attemptExchange`, NOT
        // `doExchange`. Re-entering the retry wrapper from inside one of its own
        // attempts nests the budgets — a transient failure on the fresh key
        // would return to the outer loop, which retries with the STALE key and
        // re-enters here, making the worst case (maxRetries+1)^2 requests (9 at
        // the defaults) and blowing well past the ~1.5s the spawn path can
        // afford. The outer loop owns retries for this exchange; this hop just
        // swaps the key inside the current attempt.
        return attemptExchange(freshKey, true);
      }
    }

    // ENG-8260: message text unchanged (the operator-facing manager.log line and
    // any log greps depend on it) — the classification rides alongside it.
    // `isTransientExchangeStatus` puts 500 and 429 on the transient side and
    // leaves 400/401/403/404 as verdicts, preserving today's fail-closed
    // behaviour for a genuinely rejected credential.
    throw new ApiKeyExchangeError(
      `API key exchange failed: ${errorMsg} (host=${host}, key=${obfuscated})`,
      res.status,
      isTransientExchangeStatus(res.status),
    );
  }

  const data = await res.json() as {
    token: string;
    expires_at: string;
    host_id: string;
    team_id: string;
    team_slug: string | null;
    framework?: string | null;
    host_kind?: string | null;
    claude_auth_mode?: string | null;
    anthropic_api_key_fingerprint?: string | null;
    anthropic_api_key?: string | null;
    user_email: string | null;
    supabase_url: string | null;
    supabase_anon_key: string | null;
  };

  if (!data.token) {
    // ENG-8260: a 200 with no token is a server-side fault, not a verdict on the
    // credential — classify transient so it is retried rather than treated as an
    // auth failure that stops a running session.
    throw new ApiKeyExchangeError('API key exchange returned no token', null, true);
  }

  // ENG-7152: preserve the host-level 'openrouter' mode — collapsing it to
  // 'subscription' would make the manager run the agent on the operator
  // subscription instead of OpenRouter (or, on a host with no `claude /login`,
  // skip the spawn for "not authenticated").
  const claudeAuthMode: 'subscription' | 'api_key' | 'openrouter' =
    data.claude_auth_mode === 'api_key'
      ? 'api_key'
      : data.claude_auth_mode === 'openrouter'
        ? 'openrouter'
        : 'subscription';

  // ENG-9486: keep the server's own value alongside the narrowed one. See
  // `claudeAuthModeRaw` on the ExchangeResult interface for why both exist.
  const claudeAuthModeRaw = data.claude_auth_mode ?? null;

  cachedExchange = {
    token: data.token,
    hostId: data.host_id,
    teamId: data.team_id,
    teamSlug: data.team_slug,
    framework: data.framework ?? null,
    hostKind: data.host_kind ?? null,
    claudeAuthMode,
    claudeAuthModeRaw,
    anthropicApiKeyFingerprint: data.anthropic_api_key_fingerprint ?? null,
    anthropicApiKey: data.anthropic_api_key ?? null,
    userEmail: data.user_email,
    supabaseUrl: data.supabase_url,
    supabaseAnonKey: data.supabase_anon_key,
    expiresAt: new Date(data.expires_at).getTime(),
    fetchedAt: Date.now(),
  };

  return {
    token: data.token,
    hostId: data.host_id,
    teamId: data.team_id,
    teamSlug: data.team_slug,
    framework: data.framework ?? null,
    hostKind: data.host_kind ?? null,
    claudeAuthMode,
    claudeAuthModeRaw,
    anthropicApiKeyFingerprint: data.anthropic_api_key_fingerprint ?? null,
    anthropicApiKey: data.anthropic_api_key ?? null,
    userEmail: data.user_email,
    supabaseUrl: data.supabase_url,
    supabaseAnonKey: data.supabase_anon_key,
  };
}

/**
 * ENG-7587 (ADR-0042 Option-1 pt3a): mint a PER-AGENT host key for one bound
 * agent on a POOL host, via the pt2a `POST /host/agent-key` endpoint.
 *
 * Auth is the RAW host-wide key in the body (the same primitive `/host/exchange`
 * uses) — a pool host cannot obtain a claim-less host JWT, and only a host-wide
 * key may mint (the endpoint's escalation guard). The manager's pool spawn
 * branch (pt3b) calls this and injects the returned per-agent key into the
 * agent's container as `AGT_API_KEY` instead of forwarding the shared host key,
 * so a credential reachable from tenant A's container cannot mint tenant B's
 * token. No caller yet — this is the tool pt3b wires in.
 *
 * The endpoint is gated on `host_kind='pool'` server-side, so calling it against
 * a dedicated host returns 403 (the manager only calls it when hostKind==='pool').
 * Throws on any non-2xx so the caller can fail closed rather than fall back to
 * the shared key.
 */
export async function mintAgentKey(rawHostKey: string, agentId: string): Promise<string> {
  const res = await fetch(`${requireHost()}/host/agent-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ host_key: rawHostKey, agent_id: agentId }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const errorMsg = String(body['error'] ?? res.statusText);
    throw new Error(`Per-agent key mint failed (${res.status}) for agent=${agentId}: ${errorMsg}`);
  }
  const data = (await res.json()) as { agent_key?: string; agent_id?: string; host_id?: string };
  if (!data.agent_key) {
    throw new Error(`Per-agent key mint returned no key for agent=${agentId}`);
  }
  return data.agent_key;
}

/**
 * Resolve the Bearer token from AGT_API_KEY via exchange.
 */
async function resolveAuth(): Promise<{ token: string; hostId: string }> {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error('AGT_API_KEY is not set. Export it with your host API key (tlk_...)');
  }

  const exchange = await exchangeApiKey(apiKey);
  return { token: exchange.token, hostId: exchange.hostId };
}

/**
 * Build standard request headers for authenticated API calls.
 * Team slug auto-resolves from the exchange, with AGT_TEAM
 * or config as an override.
 */
async function buildHeaders(): Promise<Record<string, string>> {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error('AGT_API_KEY is not set. Export it with your host API key (tlk_...)');
  }

  const exchange = await exchangeApiKey(apiKey);
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${exchange.token}`,
    'Content-Type': 'application/json',
    // ENG-6410: lets the API stamp audit_log.agt_cli_version for host-runtime
    // actions so bug rates can be tracked per CLI version.
    'X-Agt-Cli-Version': agtCliVersion,
  };

  // ENG-6412: carry the last heartbeat-returned config_hash so the API can stamp
  // audit_log.config_hash for host-runtime actions (config-correlation join).
  if (lastConfigHash) {
    headers['X-Config-Hash'] = lastConfigHash;
  }

  // Explicit team override takes precedence, then exchange auto-resolve
  const team = getActiveTeam() ?? exchange.teamSlug;
  if (team) {
    headers['X-Team-Slug'] = team;
  }

  return headers;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: Record<string, unknown>,
  ) {
    super((body['error'] as string) ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/**
 * Execute a fetch request with automatic retry on 401 (expired token).
 * Invalidates the cached exchange and rebuilds headers on retry.
 */
async function fetchWithRetry(
  path: string,
  method: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const baseHeaders = await buildHeaders();
  const headers: Record<string, string> = extraHeaders
    ? { ...baseHeaders, ...extraHeaders }
    : baseHeaders;
  const url = `${requireHost()}${path}`;
  const init: RequestInit = {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  const res = await fetch(url, init);

  if (res.status === 401) {
    // Token may have expired between cache check and server verification.
    // Invalidate and retry once with a fresh token.
    invalidateExchange();
    const freshBase = await buildHeaders();
    const freshHeaders: Record<string, string> = extraHeaders
      ? { ...freshBase, ...extraHeaders }
      : freshBase;
    return fetch(url, { ...init, headers: freshHeaders });
  }

  return res;
}

async function handleResponse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({})) as Record<string, unknown>;

  if (!res.ok) {
    throw new ApiError(res.status, body);
  }

  return body as T;
}

/**
 * Typed HTTP client for the Augmented API.
 */
export const api = {
  async get<T = Record<string, unknown>>(path: string): Promise<T> {
    const res = await fetchWithRetry(path, 'GET');
    return handleResponse<T>(res);
  },

  async post<T = Record<string, unknown>>(
    path: string,
    body?: unknown,
    /**
     * ENG-5688: optional headers merged on top of the standard
     * Authorization + Content-Type + X-Team-Slug set. Used by the
     * impersonation flow to send `X-Agent-Impersonation` on /stop.
     * Extra headers win on key collision, by design.
     */
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const res = await fetchWithRetry(path, 'POST', body, extraHeaders);
    return handleResponse<T>(res);
  },

  async patch<T = Record<string, unknown>>(path: string, body?: unknown): Promise<T> {
    const res = await fetchWithRetry(path, 'PATCH', body);
    return handleResponse<T>(res);
  },

  async put<T = Record<string, unknown>>(path: string, body?: unknown): Promise<T> {
    const res = await fetchWithRetry(path, 'PUT', body);
    return handleResponse<T>(res);
  },

  async del<T = Record<string, unknown>>(path: string): Promise<T> {
    const res = await fetchWithRetry(path, 'DELETE');
    return handleResponse<T>(res);
  },
};

/**
 * Resolve auth and return the host ID.
 */
export async function getHostId(): Promise<string | null> {
  const { hostId } = await resolveAuth();
  return hostId;
}
